import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { calculateAge, toIsoDate } from "../../../utils/dateUtils";
const ChildrenComparisonStep = ({
                                    existingChildren = [],
                                    newChildren = [],
                                    selectedChildren,
                                    setSelectedChildren
                                }) => {
    const { t } = useTranslation();

    const getKey = (child) =>
        `${child.firstName.trim().toLowerCase()}_${child.lastName.trim().toLowerCase()}_${child.dateBirth}`;

    const isSelectedNew = (child) =>
        selectedChildren.some((c) => getKey(c) === getKey(child));

    const handleSelect = (child, source) => {
        const key = getKey(child);
        setSelectedChildren(prev => {
            const filtered = prev.filter(c => getKey(c) !== key);
            return source === "new" ? [...filtered, child] : filtered;
        });
    };

    const existingMap = useMemo(() => {
        const map = new Map();
        existingChildren.forEach(child => {
            map.set(getKey(child), child);
        });
        return map;
    }, [existingChildren]);

    return (
        <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">{t("applications.duplicate_children_title")}</h2>

            {newChildren.map((newChild) => {
                const key = getKey(newChild);
                const existingMatch = existingMap.get(key);
                const selected = isSelectedNew(newChild) ? "new" : "existing";

                if (!existingMatch) {
                    return (
                        <div key={key} className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/80 p-4 text-gray-900 dark:border-emerald-500/40 dark:bg-emerald-900/30 dark:text-emerald-100">
                            <span className="font-semibold">{newChild.firstName} {newChild.lastName}</span> — {t("applications.no_existing_match")}
                        </div>
                    );
                }

                return (
                    <div key={key} className="mb-6 rounded-xl border border-gray-200 bg-white/90 p-4 dark:border-gray-700 dark:bg-gray-900/80">
                        <div className="grid grid-cols-3 gap-4">
                            <div
                                onClick={() => handleSelect(newChild, "existing")}
                                className={`p-2 rounded-lg border cursor-pointer transition ${
                                    selected === "existing"
                                        ? "bg-blue-100 border-blue-400 dark:bg-blue-900/40 dark:border-blue-400/60"
                                        : "hover:bg-gray-100 hover:border-gray-300 dark:hover:bg-gray-800 dark:hover:border-gray-600"
                                }`}
                            >
                                <div className="font-mono text-sm truncate text-gray-800 dark:text-gray-100">{existingMatch.firstName} {existingMatch.lastName}</div>
                                <div className="text-xs text-gray-700 italic dark:text-gray-300">
                                    {t("applications.birthdate")}:{" "}
                                    {toIsoDate(existingMatch.dateBirth)} ({calculateAge(existingMatch.dateBirth)})
                                </div>
                            </div>
                            <div className="flex items-center justify-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {t("applications.child")}
                            </div>
                            <div
                                onClick={() => handleSelect(newChild, "new")}
                                className={`p-2 rounded-lg border cursor-pointer transition ${
                                    selected === "new"
                                        ? "bg-green-100 border-green-400 dark:bg-green-900/40 dark:border-green-400/60"
                                        : "hover:bg-gray-100 hover:border-gray-300 dark:hover:bg-gray-800 dark:hover:border-gray-600"
                                }`}
                            >
                                <div className="font-mono text-sm truncate text-gray-800 dark:text-gray-100">{newChild.firstName} {newChild.lastName}</div>
                                <div className="text-xs text-gray-700 italic dark:text-gray-300">
                                    {t("applications.birthdate")}:{" "}
                                    {toIsoDate(newChild.dateBirth)} ({calculateAge(newChild.dateBirth)})
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ChildrenComparisonStep;
